/**
 * Numeric display helpers for the vehicle-spec rows on the output screens and
 * in the downloadable reports.
 *
 * Both surfaces used to render these figures as `Number(x).toLocaleString()`,
 * which produces the string "NaN" for anything that is not already a clean
 * number. That is not a hypothetical: these values reach the slice from a VIN
 * decode, from the ATC payload and from typed input, so "1,250", "6400 lbs",
 * "310 hp" and "N/A" all arrived and all printed NaN on the quote the member
 * was shown. Parsing the first number out and returning '' when there is none
 * hides the row instead, which is how moneyRow already treats an empty amount.
 *
 * Kept in one module because the screens and the reports have to agree -- the
 * download is supposed to match what is on screen (see the note in
 * docsOutput/TransactionInformation.js), so a guard applied to one and not the
 * other would itself be a divergence.
 *
 * Mirrors _whole_number() in the backend's generate_docs_html.py.
 */

const firstNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const match = String(value).replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  return match === null ? null : Number(match[0]);
};

/**
 * A figure whose row is labelled in whole units -- lbs, miles, hp. Rounds, so
 * "3100.0" prints 3,100 rather than 3,100.0, and returns '' when the value
 * carries no number at all.
 */
export const wholeNumber = (value) => {
  const parsed = firstNumber(value);
  return parsed === null ? '' : Math.round(parsed).toLocaleString();
};

/**
 * A figure that can legitimately be fractional -- MPG. Same NaN guard, but the
 * value is NOT rounded: the reports print MPG straight from the slice, so
 * rounding it here would make the screen disagree with the download.
 */
export const numericValue = (value) => {
  const parsed = firstNumber(value);
  return parsed === null ? '' : parsed.toLocaleString();
};
